import cookie_ from 'js-cookie'
import { writable } from 'svelte/store'
import type { UserLang } from '#app/lib/languages/languages'
import { getQuerystringParameter } from '#app/lib/querystring_helpers'
import { treq } from '#app/lib/treq'
import { setDeepAttribute } from '#app/lib/utils'
import type { OwnerSafeUser } from '#server/controllers/user/lib/authorized_user_data_pickers'
import type { UserUpdateSettingsParams, UserUpdateOtherParams, UserUpdateParams } from '#server/controllers/user/update'
import { notificationsList } from '#settings/lib/notifications_settings_list'
import { mainUser } from '#user/lib/main_user'
import { serializeUser, type SerializedUser } from '#users/lib/users'
import { polyglot } from './i18n'
import { solveLang } from './solve_lang'

interface SerializedMainUserExtras {
  lang: UserLang
  loggedIn: boolean
  hasAdminAccess: boolean
  hasDataadminAccess: boolean
}

export type SerializedMainUser = (OwnerSafeUser & SerializedUser & SerializedMainUserExtras)

// @ts-expect-error
export let serializedMainUser: SerializedMainUser = mainUser ? serializeMainUser(serializeUser(mainUser)) : null

export const mainUserStore = writable(serializedMainUser)

function serializeMainUser (user: OwnerSafeUser & SerializedUser & Partial<SerializedMainUserExtras>) {
  user.settings = setDefaultSettings(user)
  user.summaryPeriodicity = user.summaryPeriodicity || 20
  user.customProperties = user.customProperties || []
  user.lang = solveLang(user.language)
  const { accessLevels } = user
  user.hasAdminAccess = accessLevels.includes('admin')
  user.hasDataadminAccess = accessLevels.includes('dataadmin')
  return user
}

function setDefaultSettings (user: OwnerSafeUser & SerializedUser) {
  const { settings } = user
  const { notifications = {} } = settings
  settings.notifications = setDefaultNotificationsSettings(notifications)
  settings.contributions ??= { anonymize: true }
  return settings
}

function setDefaultNotificationsSettings (notifications: OwnerSafeUser['settings']['notifications']) {
  for (const notif of notificationsList) {
    notifications[notif] = notifications[notif] !== false
  }
  return notifications
}

// Exporting as updateUserAttribute and updateUserSetting to simplify the type interface
async function updateUser (params: UserUpdateParams) {
  const { attribute, value } = params
  const currentValue = serializedMainUser[attribute]
  try {
    serializedMainUser = setDeepAttribute(serializedMainUser, attribute, value)
    // @ts-expect-error
    if (serializedMainUser) await treq('user', 'put', 'default', { attribute, value })
    if (attribute in afterUserUpdateHooks) {
      afterUserUpdateHooks[attribute]()
    }
    // Wait for a positive answer from the server to avoid triggering calls depending on the user doc being updated
    // Example: search nearby docs only works if the user has a position set
    mainUserStore.set(serializedMainUser)
  } catch (err) {
    if (err.message !== 'already up-to-date') {
      // Rollback
      serializedMainUser = setDeepAttribute(serializedMainUser, attribute, currentValue)
      mainUserStore.set(serializedMainUser)
      throw err
    }
  }
}

export async function updateUserAttribute (attribute: UserUpdateOtherParams['attribute'], value: UserUpdateOtherParams['value']) {
  return updateUser({ attribute, value })
}

export async function updateUserSetting (settingPath: UserUpdateSettingsParams['attribute'], value: UserUpdateSettingsParams['value']) {
  return updateUser({ attribute: settingPath, value })
}

const afterUserUpdateHooks = {
  language: onUserLanguageChange,
  username: reserialize,
  picture: reserialize,
} as const

function onUserLanguageChange () {
  const lang = (mainUser && 'language' in mainUser) ? mainUser.language : null
  setNewLanguage(lang)
}

export function setNewLanguage (lang: string) {
  if (polyglot == null) return
  // @ts-expect-error `currentLocale` is missing in @types/node-polyglot
  if (lang === polyglot.currentLocale) return

  let reloadHref = window.location.href
  if (getQuerystringParameter('lang') != null) {
    // Prevent the querystring to override the language change
    // Can't just pass null to 'querystring:set' due to its limitations
    reloadHref = reloadHref
        .replace(/&?lang=\w+/, '')
        // If all there is left from the querystring is a '?', remove it
        .replace(/\?$/, '')
  }

  // The language setting is persisted as a cookie instead
  if (!mainUser) cookie_.set('lang', lang)

  location.href = reloadHref
}

function reserialize () {
  // Coupled to serializeUser implementation, which skips serialization when 'isMainUser' didn't change
  delete serializedMainUser.isMainUser
  // @ts-expect-error
  serializedMainUser = serializeMainUser(serializeUser(serializedMainUser))
}

export function mainUserHasWikidataOauthTokens () {
  if (!(serializedMainUser && 'enabledOAuth' in serializedMainUser)) return false
  const { enabledOAuth } = serializedMainUser
  return (enabledOAuth != null) && enabledOAuth.includes('wikidata')
}

export function mainUserHasAdminAccess () {
  return serializedMainUser?.hasAdminAccess === true
}

export function mainUserHasDataadminAccess () {
  return serializedMainUser?.hasDataadminAccess === true
}

export function updateMainUserListingsCount (num: number) {
  serializedMainUser.listingsCount += num
  mainUserStore.set(serializedMainUser)
}

export function updateMainUserShelvesCount (num: number) {
  serializedMainUser.shelvesCount += num
  mainUserStore.set(serializedMainUser)
}
